import { useEffect, useRef, useState } from "react";
import { addStoredDemoExpenses } from "../utils/demoStorage";
import { isDemoMode as getIsDemoMode } from "../utils/demoAuth";
import { BASE_URL } from "../utils/apiConfig";

function ReceiptScanner({
  mode,
  onExpenseAdded,
  saveToBackend,
  selectedMonth,
  selectedYear,
}) {
  const demoMode = mode ? mode === "demo" : getIsDemoMode();
  const shouldSaveToBackend =
    typeof saveToBackend === "boolean" ? saveToBackend : !demoMode;
  const [receiptFile, setReceiptFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showRawText, setShowRawText] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const getToken = () =>
    demoMode ? localStorage.getItem("demoToken") : localStorage.getItem("token");
  const getConfidenceClass = (confidence) =>
    ["high", "medium", "low"].includes(confidence) ? confidence : "low";

  const getConfidenceLabel = (confidence) => {
    const safeConfidence = getConfidenceClass(confidence);
    return `${safeConfidence.charAt(0).toUpperCase()}${safeConfidence.slice(1)} confidence`;
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage("Please upload a receipt image file.");
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setReceiptFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setScanResult(null);
    setMessage("");
    setShowRawText(false);
  };

  const scanReceipt = async () => {
    if (!receiptFile) {
      setMessage("Please choose a receipt image first.");
      return;
    }

    try {
      setScanLoading(true);
      setMessage("");

      const token = getToken();

      if (!token) {
        setScanResult(null);
        setMessage(
          demoMode
            ? "Demo session expired. Please log in to the demo again."
            : "Session expired. Please log in again."
        );
        return;
      }

      const formData = new FormData();
      formData.append("receipt", receiptFile);

      const res = await fetch(`${BASE_URL}/api/ocr/receipt`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        setScanResult(null);
        setMessage(data.error || "Receipt scan failed.");
        return;
      }

      setScanResult(data);
      setMessage(
        demoMode
          ? "Demo receipt scanned successfully."
          : "Receipt scanned successfully."
      );
    } catch {
      setMessage("Backend not reachable. Please check node server.js.");
    } finally {
      setScanLoading(false);
    }
  };

  const addExpense = async () => {
    if (!scanResult || !scanResult.amount || Number(scanResult.amount) <= 0) {
      setMessage("Scan result does not contain a valid amount.");
      return;
    }

    try {
      setAddLoading(true);
      setMessage("");

      if (!shouldSaveToBackend) {
        addStoredDemoExpenses(
          [
            {
              text:
                scanResult.merchant ||
                scanResult.description ||
                "Demo receipt expense",
              amount: Number(scanResult.amount),
              category: scanResult.category || "others",
              source: "ocr",
            },
          ],
          selectedMonth,
          selectedYear
        );

        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }

        setReceiptFile(null);
        setPreviewUrl("");
        setScanResult(null);
        setShowRawText(false);
        setMessage("Demo OCR expense added successfully.");

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        if (onExpenseAdded) {
          onExpenseAdded();
        }

        return;
      }

      const res = await fetch(`${BASE_URL}/api/expenses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          text: scanResult.description || "Receipt expense",
          amount: Number(scanResult.amount),
          category: scanResult.category || "others",
          source: "ocr",
          month: selectedMonth,
          year: selectedYear,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setMessage(data.error || "Failed to add OCR expense.");
        return;
      }

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setReceiptFile(null);
      setPreviewUrl("");
      setScanResult(null);
      setShowRawText(false);
      setMessage("OCR expense added successfully.");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (onExpenseAdded) {
        onExpenseAdded();
      }
    } catch {
      setMessage("Backend not reachable. Please check node server.js.");
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <div className="receipt-scanner-card">
      <div className="receipt-scanner-header">
        <div>
          <h3>📸 Receipt Scanner</h3>
          <p>Upload a receipt image and convert it into an expense.</p>
        </div>
        <span>Google Vision OCR</span>
      </div>

      <div className="receipt-upload-row">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
        />

        <button
          type="button"
          onClick={scanReceipt}
          disabled={scanLoading || !receiptFile}
        >
          {scanLoading ? "Scanning..." : "Scan Receipt"}
        </button>
      </div>

      {previewUrl && (
        <div className="receipt-preview-wrap">
          <img src={previewUrl} alt="Receipt preview" />
        </div>
      )}

      {scanLoading && (
        <div className="receipt-loading-card">
          <span className="receipt-loading-dot"></span>
          <p>Scanning receipt...</p>
        </div>
      )}

      {message && (
        <p
          className={`receipt-message ${
            message.toLowerCase().includes("success") ? "success" : "error"
          }`}
        >
          {message}
        </p>
      )}

      {scanResult && (
        <div
          className={`receipt-result-card confidence-${getConfidenceClass(
            scanResult.confidence
          )}`}
        >
          <div className="receipt-result-top">
            <div>
              <div className="receipt-badge-row">
                <span className="receipt-badge">OCR Parsed</span>
                <span className="receipt-badge vision">Google Vision</span>
                <span
                  className={`receipt-confidence-badge ${getConfidenceClass(
                    scanResult.confidence
                  )}`}
                >
                  {getConfidenceLabel(scanResult.confidence)}
                </span>
              </div>

              <h4>Receipt Result</h4>
              <p>Review before adding to expenses</p>
            </div>

            <span className="receipt-source-pill">OCR AI / Vision</span>
          </div>

          <div className="receipt-result-main">
            <div className="receipt-amount-panel">
              <span>Detected Amount</span>
              <strong>
                ₹{Number(scanResult.amount || 0).toLocaleString("en-IN")}
              </strong>
              {getConfidenceClass(scanResult.confidence) === "low" && (
                <small>Please verify this amount before adding.</small>
              )}
            </div>

            <div className="receipt-category-panel">
              <span>Category</span>
              <strong>{scanResult.category || "others"}</strong>
            </div>
          </div>

          <div className="receipt-description-box">
            <span>Shop / Restaurant</span>
            <p>{scanResult.merchant || scanResult.description || "Receipt expense"}</p>
          </div>

          <div className="receipt-actions">
            <button type="button" onClick={addExpense} disabled={addLoading}>
              {addLoading ? "Adding..." : "Add Expense"}
            </button>

            <button
              type="button"
              className="receipt-secondary-btn"
              onClick={() => setShowRawText((current) => !current)}
            >
              {showRawText ? "Hide Raw Text" : "Show Raw Text"}
            </button>
          </div>

          {showRawText && (
            <pre className="receipt-raw-text">{scanResult.rawText}</pre>
          )}
        </div>
      )}
    </div>
  );
}

export default ReceiptScanner;
